import React, {
  useState,
  useRef,
  useEffect,
  Dispatch,
  SetStateAction,
  SyntheticEvent,
} from "react";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Button } from "@mui/material";
import DefaultImage from  "../../assets/SuntecNewLogo.png";


interface SimpleImageSliderProps {
  images: string[];
  rotationAngle: number;
  setImage: Dispatch<SetStateAction<HTMLImageElement | HTMLElement | null>>;
}

const SimpleImageSlider: React.FC<SimpleImageSliderProps> = ({
  images,
  rotationAngle,
  setImage,
}) => {
  const imageRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    setImage(imageRef.current);
  }, [setImage]);

  const [currentImageIndex, setCurrentImageIndex] = useState(images.length - 1);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPosition, setStartPosition] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const handleMouseMove = (event: MouseEvent) => {
      if (!isDragging) return;

      const offsetX = event.clientX - startPosition.x;
      const offsetY = event.clientY - startPosition.y;

      setOffset({
        x: offsetX,
        y: offsetY,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, startPosition]);

  const handleMouseDown = (event: React.MouseEvent<HTMLImageElement>) => {
    setIsDragging(true);
    setStartPosition({
      x: event.clientX - offset.x,
      y: event.clientY - offset.y,
    });
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "ArrowUp" || event.key === "ArrowDown") {
      event.preventDefault();
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % images.length);
    setScale(1); // Reset scale when changing images
    setOffset({ x: 0, y: 0 }); // Reset offset when changing images
  };

  const handlePreviousImage = () => {
    setCurrentImageIndex(
      (prevIndex) => (prevIndex - 1 + images.length) % images.length
    );
    setScale(1); // Reset scale when changing images
    setOffset({ x: 0, y: 0 }); // Reset offset when changing images
  };

  const handleMouseEnter = () => {
    document.body.style.overflow = "hidden";
  };

  const handleMouseLeave = () => {
    document.body.style.overflow = "auto";
  };

  const onImageLoadError = (event: SyntheticEvent<HTMLImageElement, Event>) => {
    event.currentTarget.src = DefaultImage;
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Button
        onClick={handlePreviousImage}
        disabled={currentImageIndex === 0}
        variant="text"
        color="inherit"
      >
        <ArrowBackIosIcon fontSize="small" />
      </Button>
      <img
        ref={imageRef}
        src={images[currentImageIndex] || DefaultImage}
        onError={onImageLoadError}
        alt={`Image ${currentImageIndex}`}
        style={{
          width: "70%",
          height: "100%",
          borderRadius: "8px",
          transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale}) rotate(${rotationAngle}deg)`,
          objectFit: "contain",
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
        }}
        onMouseDown={handleMouseDown}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      />
      <Button
        onClick={handleNextImage}
        disabled={currentImageIndex === images.length - 1}
        variant="text"
        color="inherit"
      >
        <ArrowForwardIosIcon fontSize="small" />
      </Button>
    </div>
  );
};

export default SimpleImageSlider;
